import moment from 'moment';
import { orderBy, get } from 'lodash';

const subjectCode = {
  tech: 'T', // All qualifying TECHNICAL CPE courses not otherwise described
  ethics_state: 'E', // Ethics (4 hours required each renewal period; Oregon-specifc course required for frst renewal)
  ethics_general: 'E', // Ethics (4 hours required each renewal period; Oregon-specifc course required for frst renewal)
  a_a: 'M', // Municipal Auditing (24 hours required for Municipal Auditors)
  nontech: 'NT', // NON-TECHNICAL (limited to 16 hours Active/8 hours Inactive) see instructions for non-technical CPE categories
  3: 'B',
  4: 'B', // CPE Course instructor, author of published books or articles, or discussion leader (may not exceed 50% of the total CPE requirement either separately or combined)
  9: 'NL' // Nano-learning (may not exceed 10% of the total CPE requirement)
};

function categoryTotal(license, category) {
  let total = get(license, `hour_categories.${category}.cycle.completed`, 0);
  const carryOverCategoryTotal = get(license, `carry_over_applied.${category}.total`, 0);
  const deliveryMethod3 = get(license, `delivery_methods_applied.${category}.3`, 0);
  const deliveryMethod4 = get(license, `delivery_methods_applied.${category}.4`, 0);

  total -= carryOverCategoryTotal;
  total -= deliveryMethod3;
  total -= deliveryMethod4;

  return total > 0 ? total : 0;
}

function annuals($filter, license) {
  return Object.keys(license.annuals)
    .sort()
    .map(lic_yr => {
      const { issue_date } = license;
      const { completed, annual_date } = license.annuals[lic_yr];

      const dateRange = $filter('CycleDuration')(annual_date, 1, issue_date);

      return [
        {
          text: `Total CPE hours from ${dateRange}:`,
          alignment: 'right',
          lineHeight: 2,
          style: 'tableTextBold'
        },
        {
          text: `${completed.toFixed(1)}`,
          lineHeight: 2,
          style: 'tableText'
        }
      ];
    });
}

function carryOver(license) {
  const total = Object.keys(get(license, `carry_over_applied.total`, {})).reduce((sum, deliveryMethod) => {
    sum += license.carry_over_applied.total[deliveryMethod];
    return sum;
  }, 0);

  return [
    {
      text: 'Carry forward hours applied from last renewal period :\n(max 20 active/8 inactive)',
      alignment: 'right',
      style: 'tableTextBold'
    },
    {
      text: `${total.toFixed(1)}`,
      style: 'tableText'
    }
  ];
}

function headerText({ first, last }, { license_number }) {
  return `Name: ${first} ${last}
          License Number: ${license_number || 'N/A'}`;
}

function template($filter, license, certificates, profile) {
  return {
    pageSize: 'LETTER',
    pageMargins: [25, 25, 25, 40],
    info: {
      title: 'Oregon Certified Public Accountant',
      author: `${profile.first} ${profile.last}`,
      creator: 'Prolaera',
      producer: 'Prolaera'
    },
    content: [
      {
        text: headerText(profile, license),
        style: 'pageLabel'
      },
      {
        style: 'tableExample',
        layout: {
          hLineColor: function(i, node) {
            return i < 3 || i === node.table.body.length ? '' : '#9cc2e5';
          },
          vLineColor: function(i, node, row) {
            if (row < 2) return '';

            return i < 1 || i === node.table.widths.length ? '' : '#9cc2e5';
          }
        },
        table: {
          widths: [10, 150, 60, 60, 40, 40, '*', 40],
          body: [
            [
              {
                fillColor: 'black',
                bold: true,
                color: 'white',
                colSpan: 8,
                text: 'CONTINUING EDUCATION REPORT (List programs in chronological order)'
              },
              {},
              {},
              {},
              {},
              {},
              {},
              {}
            ],
            [
              {
                colSpan: 2,
                text: 'COURSE TITLE',
                style: 'tableHeader'
              },
              {},
              {
                text: 'SPONSOR',
                style: 'tableHeader'
              },
              {
                text: 'DATE Completed',
                style: 'tableHeader'
              },
              {
                text: '*CPE Type (see below)',
                style: 'tableHeader'
              },
              {
                text: 'Live (L) or Self- study (S)',
                style: 'tableHeader'
              },
              {
                text: 'NASBA National Registry # (self- study only)',
                style: 'tableHeader'
              },
              {
                text: 'HOURS earned',
                style: 'tableHeader'
              }
            ],
            ...certificates
          ]
        }
      },
      {
        style: 'basicPageText',
        text:
          'If you need additional space, attach a photocopy of this report with additional CPE courses. A PDF fillable form can be found on the Board website.\nA computer generated form is acceptable only if it is produced in an identical format.'
      },
      {
        layout: {
          hLineColor: function(i, node) {
            return i < 2 || i === node.table.body.length ? '' : '#9cc2e5';
          },
          vLineColor: function(i, node, row) {
            if (row < 1) return '';

            return i < 1 || i === node.table.widths.length ? '' : '#9cc2e5';
          }
        },
        table: {
          widths: [50, '*', 60],

          body: [
            [
              {
                fillColor: '#deeaf6',
                text: '*CPE Type',
                style: 'tableHeader'
              },
              {
                fillColor: '#deeaf6',
                text: 'CALCULATE TOTAL CPE HOURS CLAIMED FOR EACH CPE TYPE',
                style: 'tableHeader'
              },
              {
                fillColor: '#deeaf6',
                text: 'HOURS',
                style: 'tableHeader'
              }
            ],
            [
              {
                text: 'T',
                style: 'tableBoldCenter'
              },
              {
                text: 'All qualifying TECHNICAL CPE courses not otherwise described',
                style: 'tableText'
              },
              {
                text: `${categoryTotal(license, 'tech').toFixed(1)}`,
                style: 'tableText'
              }
            ],
            [
              {
                text: 'E',
                style: 'tableBoldCenter'
              },
              {
                text:
                  'Ethics (4 hours required each renewal period; Oregon-specific course required for first renewal)',
                style: 'tableText'
              },
              {
                text: `${(categoryTotal(license, 'ethics_state') + categoryTotal(license, 'ethics_general')).toFixed(1)}`,
                style: 'tableText'
              }
            ],
            [
              {
                text: 'M',
                style: 'tableBoldCenter'
              },
              {
                text: 'Municipal Auditing (24 hours required for Municipal Auditors)',
                style: 'tableText'
              },
              {
                text: `${categoryTotal(license, 'a_a').toFixed(1)}`,
                style: 'tableText'
              }
            ],
            [
              {
                text: 'NT',
                style: 'tableBoldCenter'
              },
              {
                text: [
                  'NON-TECHNICAL (limited to 16 hours Active/8 hours Inactive)',
                  {
                    text: ' see instructions for non-technical CPE categories',
                    italics: true,
                    bold: true
                  }
                ],
                style: 'tableText'
              },
              {
                text: `${categoryTotal(license, 'nontech').toFixed(1)}`,
                style: 'tableText'
              }
            ],
            [
              {
                text: 'B',
                style: 'tableBoldCenter'
              },
              {
                text:
                  'CPE Course instructor, author of published books or articles, or discussion leader\n(may not exceed 50% of the total CPE requirement either separately or combined)',
                style: 'tableText'
              },
              {
                text: `${(
                  license.delivery_methods_applied.total['3'] + license.delivery_methods_applied.total['4']
                ).toFixed(1)}`,
                style: 'tableText'
              }
            ],
            [
              {
                text: 'NL',
                style: 'tableBoldCenter'
              },
              {
                text: 'Nano-learning (may not exceed 10% of the total CPE requirement)',
                style: 'tableText'
              },
              {
                text: `${license.delivery_methods_applied.total['9'].toFixed(1)}`,
                style: 'tableText'
              }
            ]
          ]
        }
      },
      '\n',
      {
        columns: [
          {
            width: 200,
            text: [
              '\n',
              {
                text: 'A MINIMUM OF 24 HOURS must be obtained in each year of the renewal period.',
                bold: true,
                fontSize: 8,
                alignment: 'center',
                color: 'red'
              },
              '\n\n',
              {
                fontSize: 8,
                text:
                  'CPE Courses completed on or before June 30, but after the date you submit this renewal, may be included on the CPE report.'
              },
              {
                fontSize: 8,
                text:
                  'If you are unable to attend or complete a course before June 30, or a course is cancelled, provide written notice to the Board describing the credits to be removed from your CPE report.',
                bold: true
              }
            ]
          },
          {
            width: 20,
            text: ''
          },
          {
            width: '*',
            layout: {
              hLineColor: function(i, node, column) {
                return column < 1 ? 'white' : '#aecdea';
              },
              vLineColor: function(i, node, row) {
                return i === 0 ? 'white' : '#aecdea';
              }
            },
            table: {
              widths: ['*', 60],
              body: [
                [
                  {
                    fillColor: '#deeaf6',
                    text: '(calculate subtotals from this page and all attached pages)',
                    alignment: 'right',
                    italics: true,
                    bold: true,
                    fontSize: 8
                  },
                  {
                    fillColor: '#deeaf6',
                    text: 'HOURS',
                    bold: true,
                    style: 'tableHeader'
                  }
                ],
                ...annuals($filter, license),
                carryOver(license),
                [
                  {
                    text: 'TOTAL CPE HOURS REPORTED:',
                    alignment: 'right',
                    lineHeight: 2,
                    style: 'tableTextBold'
                  },
                  {
                    text: `${license.cycleHours.completed.toFixed(1)}`,
                    lineHeight: 2,
                    style: 'tableText'
                  }
                ],
                [
                  {
                    text: '(STAFF USE ONLY)',
                    alignment: 'right',
                    lineHeight: 2,
                    style: 'tableTextBold'
                  },
                  {
                    text: '',
                    lineHeight: 2,
                    fillColor: '#f2f2f2',
                    style: 'tableText'
                  }
                ]
              ]
            }
          }
        ]
      },
      '\n',
      {
        columns: [
          {
            width: '*',
            fontSize: 9,
            text: ['Questions? Please contact (503) 378-4181 or ', { text: 'boa.info@oregon.gov', color: 'blue' }]
          },
          {
            width: 200,
            alignment: 'center',
            text: `${moment().year()} CPA/PA Permit Renewal`
          }
        ]
      }
    ],
    styles: {
      basicPageText: {
        fontSize: 8,
        alignment: 'center'
      },
      pageLabel: {
        fontSize: 12,
        bold: false,
        alignment: 'left',
        margin: [0, 0, 0, 10]
      },
      tableHeader: {
        bold: true,
        fontSize: 10,
        alignment: 'center',
        color: 'black'
      },
      tableText: {
        fontSize: 8,
        color: 'black'
      },
      tableBoldCenter: {
        bold: true,
        alignment: 'center',
        fontSize: 8,
        color: 'black'
      },
      tableTextBold: {
        bold: true,
        fontSize: 8,
        color: 'black'
      }
    }
  };
}

const or_cpa = ($filter, license, certsDict, profile) => {
  const years = Object.keys(license.annuals)
    .sort()
    .filter(key => key.startsWith('hours_') || !isNaN(key));

  const certs = years.reduce((arr, year) => {
    Object.keys(license.annuals[year].certificates_applied).forEach(cert_id => {
      arr.push({
        ...certsDict[cert_id],
        sortDate: new Date(certsDict[cert_id].date).getTime(),
        certificates_applied: license.annuals[year].certificates_applied[cert_id]
      });
    });
    return arr;
  }, []);

  const sortedCerts = orderBy(certs, ['sortDate'], ['asc']);

  const certificates = sortedCerts.reduce((arr, certificate, index) => {
    const { certificates_applied, cert, date, sponsor, sponsors, delivery_uid } = certificate;
    const delivery = [0, 8].includes(delivery_uid) ? 'S' /*Self-Study or QAS*/ : 'L';

    const sponsorName = sponsor || get(sponsors, 'name', '');
    const nationalRegistry = get(sponsors, 'nationalRegistry', '');

    Object.keys(certificates_applied).forEach(key => {
      const cpeType = subjectCode[delivery_uid] || subjectCode[key] || '';
      const credits = certificates_applied[key];

      if (credits <= 0) return;

      // [Index, 'COURSE TITLE', 'SPONSOR', DATE Completed, *CPE Type (see below), Live (L) or Self- study (S), NASBA National Registry # (self- study only), HOURS earned]
      const item = [
        { text: `${index + 1}`, style: 'tableText' },
        { text: `${cert}`, style: 'tableText' },
        { text: `${sponsorName || ''}`, style: 'tableText' },
        { text: date ? moment(date).format('L') : '', alignment: 'center', style: 'tableText' },
        { text: cpeType, style: 'tableText' },
        { text: `${delivery}`, style: 'tableText' },
        { text: `${nationalRegistry || ''}`, style: 'tableText' },
        { text: `${credits.toFixed(1)}`, style: 'tableText' }
      ];

      arr.push(item);
    });

    return arr;
  }, []);

  const params = template($filter, license, certificates, profile);

  return params;
};

export { or_cpa };



// WEBPACK FOOTER //
// ./src/app/components/users/reports/renewals/templates/or_cpa.js